/**
 * rb-preview.js — the page on the desk.
 *
 * Paints the resume with the shared RbRender.render() (target 'preview', with
 * placeholder highlighting), scales it to the desk, and draws where the pages
 * will break when printed. Never mutates the ResumeModel.
 *
 * Geometry. The .rb-doc is a fixed-width page (794 px for A4) positioned at the
 * top-left of a wrapper whose box is the SCALED size, so the desk scrolls exactly
 * over what is visible. The old version scaled from "top center" inside a box as
 * wide as the unscaled page, which pushed the page off to one side and made the
 * whole tool wider than the screen at 1024–1440 px (audit 2026-10-09); "fit" also
 * measured a box sized by the page itself, so every press shrank it a bit more.
 * Here fit reads the DESK, which the layout sizes.
 *
 * Page breaks. Printing uses 12 mm top and bottom margins on every page after
 * the first (rb-print.js), so a page holds `pageH − 2·M` of the flow after the
 * first one. Entries avoid splitting, so the real break can land a little
 * earlier; the marks say "about here", which is what the person needs.
 *
 * Depends on: RbRender, RbState.
 * globalThis.RbPreview
 */
(function () {
    'use strict';

    var PRINT_MARGIN = 45;   /* 12 mm at 96 dpi — keep in sync with rb-print.js */

    var _paper = null, _desk = null, _pageInfo = null, _zoomOut = null;
    var _zoom = 1, _autoFit = true;
    var _pageW = 794, _pageH = 1123, _docH = 1123;
    var _labels = { page: 'Page :n', pages: ':n pages', sample: 'Example' };
    var _ro = null;

    function mount(refs) {
        _paper = refs.paperEl;
        _desk = refs.deskEl;
        _pageInfo = refs.pageInfoEl || null;
        _zoomOut = refs.zoomOutEl || null;
        if (refs.labels) Object.assign(_labels, refs.labels);
        if (_desk && typeof ResizeObserver === 'function') {
            var last = 0;
            _ro = new ResizeObserver(function () {
                var w = _desk.clientWidth;
                if (Math.abs(w - last) < 2) return;
                last = w;
                if (_autoFit) fit(true);
            });
            _ro.observe(_desk);
        }
    }

    function pagesFor(h) {
        if (h <= _pageH + 2) return 1;
        return 1 + Math.ceil((h - (_pageH - PRINT_MARGIN)) / (_pageH - 2 * PRINT_MARGIN));
    }

    function repaint(model, opts) {
        if (!_paper) return;
        opts = opts || {};
        var pg = globalThis.RbState.pageSize(model.meta && model.meta.pageSize);
        _pageW = pg.w;
        _pageH = pg.h;
        _paper.innerHTML = globalThis.RbRender.render(model, {
            target: 'preview', marks: true, dateFormat: opts.dateFormat, rtl: !!opts.rtl
        });
        var doc = _paper.firstElementChild;
        _docH = doc ? Math.max(_pageH, doc.offsetHeight || doc.scrollHeight || _pageH) : _pageH;
        if (model.meta && model.meta.sample) {
            var tag = document.createElement('div');
            tag.className = 'rbx-sample-tag';
            tag.textContent = _labels.sample;
            _paper.appendChild(tag);
        }
        apply();
    }

    function apply() {
        if (!_paper) return;
        var doc = _paper.firstElementChild;
        if (!doc) return;
        doc.style.transform = 'scale(' + _zoom + ')';
        _paper.style.width = Math.round(_pageW * _zoom) + 'px';
        _paper.style.height = Math.round(_docH * _zoom) + 'px';
        /* page-break marks */
        Array.prototype.forEach.call(_paper.querySelectorAll('.rbx-break'), function (b) { b.remove(); });
        var pages = pagesFor(_docH);
        var y = _pageH - PRINT_MARGIN;
        for (var n = 2; n <= pages; n++) {
            var mark = document.createElement('div');
            mark.className = 'rbx-break';
            mark.style.top = Math.round(y * _zoom) + 'px';
            var s = document.createElement('span');
            s.textContent = _labels.page.replace(':n', n);
            mark.appendChild(s);
            _paper.appendChild(mark);
            y += _pageH - 2 * PRINT_MARGIN;
        }
        if (_pageInfo) {
            _pageInfo.textContent = _labels.pages.replace(':n', pages);
            _pageInfo.dataset.pages = String(pages);
        }
        if (_zoomOut) _zoomOut.textContent = Math.round(_zoom * 100) + '%';
    }

    function setZoom(z) {
        _autoFit = false;
        _zoom = Math.max(0.3, Math.min(1.6, Math.round(z * 100) / 100));
        apply();
        return _zoom;
    }
    function zoom() { return _zoom; }
    function zoomIn() { return setZoom(_zoom + 0.1); }
    function zoomOut() { return setZoom(_zoom - 0.1); }

    /* Fit the page width into the desk (its padding excluded). */
    function fit(silent) {
        if (!_desk) return _zoom;
        _autoFit = true;
        var cs = getComputedStyle(_desk);
        var avail = _desk.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
        if (avail > 40) _zoom = Math.max(0.3, Math.min(1.25, Math.floor(avail / _pageW * 100) / 100));
        apply();
        return _zoom;
    }

    function pageCount() {
        return _pageInfo ? parseInt(_pageInfo.dataset.pages || '1', 10) : pagesFor(_docH);
    }

    /* Thumbnail HTML for a template card: a full render of the person's own
       content in that template (A4 so every card scales the same). */
    function thumbHtml(model, templateId, opts) {
        var m = globalThis.RbState.clone(model);
        m.meta.template = templateId;
        m.meta.pageSize = 'a4';
        return globalThis.RbRender.render(m, {
            target: 'thumb', dateFormat: (opts && opts.dateFormat) || 'MM/YYYY', rtl: !!(opts && opts.rtl)
        });
    }

    globalThis.RbPreview = {
        PRINT_MARGIN: PRINT_MARGIN,
        mount: mount,
        repaint: repaint,
        setZoom: setZoom,
        zoom: zoom,
        zoomIn: zoomIn,
        zoomOut: zoomOut,
        fit: fit,
        pageCount: pageCount,
        pagesFor: pagesFor,
        thumbHtml: thumbHtml
    };
})();
